import { EditorialSpreadPublicationControlsV1 } from "./EditorialSpreadPublicationControlsV1";
/**
 * Editorial Authoring V2 — PROCESS_STORY authoring (BUILD 8, source-first
 * correction).
 *
 * Rendered by EditorialSpreadWorkspaceV2.jsx in place of the generic
 * workspace body when block.master_type === "PROCESS_STORY".
 * Self-contained: owns its own modal chrome, save flow and save-state
 * display.
 *
 * PROCESS_STORY explains the LOGIC of a process (what changes → why it
 * matters → which parameter matters) — it is NOT STEP_BY_STEP, not a
 * photographic sequence, not a recipe. There is deliberately no
 * per-phase image UI anywhere in this file.
 *
 * ── PRODUCT FLOW (this correction) ───────────────────────────────────
 * The Owner pastes the full technology text once, clicks "Распознать
 * технологию", and the deterministic local recognizer
 * (structureProcessStorySource, @/lib/editorialProcessStoryAuthoring.mjs
 * — no provider call, no network) structures it into introduction /
 * phases / conclusion. The Owner reviews and corrects the RESULT;
 * manually building phases from scratch (the old BUILD 8 flow) is now
 * a secondary, visually de-emphasized correction path, never the
 * primary task. Reordering/adding/removing phases still works exactly
 * as before — it's just not what the workspace leads with.
 *
 * Mirrors the HERO/EDITORIAL_STORY source-edit safety contract exactly
 * (same one those two got QA-corrected to): recognizedSourceText is a
 * snapshot of the source that produced the current introduction/
 * phases; sourceText changing away from that snapshot marks the
 * result STALE ("Текст изменён — распознайте структуру заново.") and
 * disables Save until the Owner re-recognizes — Save can never
 * silently pair a changed source with stale structure. Reopening the
 * source textarea via "Изменить исходный текст" does NOT by itself
 * invalidate anything; only an actual text change does.
 *
 * ── STORAGE CONTRACT (confirmed) ──────────────────────────────────────
 *   content.process_story = {
 *     source_text, eyebrow, title, introduction, caption, conclusion,
 *     main_image, mobile_image, phases
 *   }
 *   phase:  { id, label, title, text, parameter }
 *   image:  { media_id, url, alt, focal_x, focal_y, zoom }
 * source_text is a genuinely new field inside the existing free-form
 * process_story dict — no backend/schema migration, since process_story
 * was already an arbitrary structured object.
 *
 * Legacy top-level content fields are never overwritten — only read,
 * once, as a fallback on initial load:
 *   title        = process_story.title        ?? content.headline
 *   introduction = process_story.introduction  ?? content.lead
 *   caption      = process_story.caption       ?? content.caption
 * eyebrow and conclusion have no legacy top-level equivalent (?? ""
 * only). Recognition NEVER touches eyebrow/title/caption — those stay
 * fully manual, same treatment as HERO/EDITORIAL_STORY's eyebrow.
 *
 * ── COMPATIBILITY (existing saved PROCESS_STORY) ─────────────────────
 * If a block already has structured phases/introduction/conclusion but
 * no source_text (pre-dates this correction), it is shown as an
 * already-recognized result immediately — no forced re-recognition, no
 * fabricated source_text. recognizedSourceText is initialized to
 * whatever source_text already is (including ""), so an empty
 * source_text next to existing structured content is never flagged
 * stale.
 *
 * hasLegacyStructuredResult (Owner QA fix) is the live condition
 * "structured result exists AND source_text is still empty" — it gets
 * this legacy case a prominent, explicit CTA ("Вставить исходный
 * текст технологии") above the structured result, more visible than
 * the small "Изменить исходный текст" link used once a block already
 * has real source_text. Opening the source editor from that CTA only
 * flips sourceEditing — it never touches introduction/phases/
 * conclusion/images/caption/eyebrow/title, and Save stays enabled the
 * whole time the reopened textarea remains empty (isStale is false
 * when both sides are ""). The existing structured result is only
 * ever replaced once the Owner pastes real text and explicitly clicks
 * "Распознать технологию".
 *
 * ── URL CONTRACT (confirmed) ─────────────────────────────────────────
 * mediaRecordUrl(media) — plus a file_url/url fallback — resolves a
 * freshly picked/uploaded media OBJECT. resolveMediaUrl(urlString)
 * takes a URL STRING only and is used purely at render time — never
 * written back into saved content.
 *
 * ── CROP / PREVIEW (unchanged from the prior BUILD 8 pass) ───────────
 * 4:5 crop frame for both main and mobile image. Desktop: 45/55 grid,
 * image+caption on the LEFT. Mobile order: eyebrow → title →
 * introduction → image → phases → conclusion → CAPTION LAST. A
 * PROCESS_STORY with no active image renders the confirmed text-only
 * mode instead of an empty box. None of this changed in this
 * correction.
 *
 * Provider calls = 0. AI cost = $0.
 */
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { mediaRecordUrl, resolveMediaUrl } from "@/lib/editorialMedia.mjs";
import { editorialImageStyle } from "@/lib/editorialReaderLayout.mjs";
import { structureProcessStorySource } from "@/lib/editorialProcessStoryAuthoring.mjs";
import { MediaLibraryPickerV2, MEDIA_UPLOAD } from "./EditorialMediaPickerV2";
import { EditorialCropV2 } from "./EditorialCropV2";

const CROP_ASPECT = "4/5";
const MAIN_FRAME_LABEL = "Основное изображение · 4:5";
const MOBILE_FRAME_LABEL = "Мобильное изображение · 4:5";

const DESKTOP_MAX_HEIGHT = 680;
const MOBILE_MAX_HEIGHT = 420;

// Confirmed Reader colors — fixed, no palette/background settings.
const BG = "#EFE6D5";
const EYEBROW_COLOR = "#8A6A46";
const TITLE_COLOR = "#2A1D14";
const INTRO_COLOR = "#5B4326";
const PHASE_NUMBER_COLOR = "#A67B4A";
const PHASE_LABEL_COLOR = "#8A6A46";
const PHASE_TITLE_COLOR = "#2A1D14";
const PHASE_TEXT_COLOR = "#2A1D14";
const PARAMETER_LABEL_COLOR = "#5B4326";
const PARAMETER_TEXT_COLOR = "#2A1D14";
const CONCLUSION_COLOR = "#3E2E20";
const CONCLUSION_BORDER_COLOR = "#A67B4A";
const CAPTION_COLOR = "#5B4326";

function destinationLines(volume) {
  if (!volume) {
    return { title: "Место публикации будет указано здесь", access: null };
  }
  return {
    title: volume.title,
    access: volume.product_id ? "Доступ: по связанному продукту" : "Доступ: не указан",
  };
}

function normalizeImage(raw) {
  if (!raw || (!raw.media_id && !raw.url)) return null;
  return {
    media_id: raw.media_id || null,
    url: raw.url || null,
    alt: raw.alt || "",
    focal_x: raw.focal_x ?? 0.5,
    focal_y: raw.focal_y ?? 0.5,
    zoom: raw.zoom ?? 1,
  };
}

let idSeq = 0;
function newId(prefix) {
  idSeq += 1;
  return `${prefix}-${Date.now().toString(36)}-${idSeq}`;
}

// Reader-compatible fallback on initial load — see file header.
function deriveInitial(content) {
  const c = content || {};
  const ps = c.process_story || {};
  const sourceText = String(ps.source_text || "");
  return {
    sourceText,
    recognizedSourceText: sourceText, // never flag an existing legacy result as stale
    eyebrow: ps.eyebrow ?? "",
    title: ps.title ?? c.headline ?? "",
    introduction: ps.introduction ?? c.lead ?? "",
    caption: ps.caption ?? c.caption ?? "",
    conclusion: ps.conclusion ?? "",
    mainImage: normalizeImage(ps.main_image),
    mobileImage: normalizeImage(ps.mobile_image),
    phases: Array.isArray(ps.phases)
      ? ps.phases.map((p, i) => ({
        id: p?.id || `phase-legacy-${i}`,
        label: p?.label || "",
        title: p?.title || "",
        text: p?.text || "",
        parameter: p?.parameter || "",
      }))
      : [],
  };
}

function ProcessImage({ image, maxHeight }) {
  if (!image?.url) return null;
  const resolvedUrl = resolveMediaUrl(image.url);
  return (
    <div className="relative overflow-hidden bg-espresso/10" style={{ aspectRatio: CROP_ASPECT, maxHeight: `${maxHeight}px` }}>
      <img src={resolvedUrl} alt={image.alt || ""} className="absolute inset-0 h-full w-full" style={editorialImageStyle(image)} />
    </div>
  );
}

function PhasesPreview({ phases }) {
  const filled = phases.filter((p) => p.label || p.title || p.text || p.parameter);
  if (!filled.length) return null;
  return (
    <div className="mt-4 divide-y divide-espresso/10">
      {filled.map((p, i) => (
        <div key={p.id} className="py-3">
          <div className="font-serif text-lg" style={{ color: PHASE_NUMBER_COLOR }}>
            {String(i + 1).padStart(2, "0")}
          </div>
          {p.label && (
            <div className="mt-1 text-[10px] uppercase tracking-widest" style={{ color: PHASE_LABEL_COLOR }}>{p.label}</div>
          )}
          {p.title && (
            <div className="mt-1 font-serif text-base" style={{ color: PHASE_TITLE_COLOR }}>{p.title}</div>
          )}
          {p.text && (
            <div className="mt-1 font-serif text-sm leading-relaxed whitespace-pre-wrap" style={{ color: PHASE_TEXT_COLOR }}>{p.text}</div>
          )}
          {p.parameter && (
            <div className="mt-2 inline-flex items-center gap-1 rounded-full border border-espresso/15 px-2 py-0.5 text-[10px]">
              <span style={{ color: PARAMETER_LABEL_COLOR }}>Параметр:</span>
              <span style={{ color: PARAMETER_TEXT_COLOR }}>{p.parameter}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ConclusionPreview({ conclusion }) {
  if (!conclusion) return null;
  return (
    <div className="mt-4 border-l-2 pl-3 font-serif text-sm italic" style={{ color: CONCLUSION_COLOR, borderColor: CONCLUSION_BORDER_COLOR }}>
      {conclusion}
    </div>
  );
}

function ProcessTextBlock({ eyebrow, title, introduction, phases, conclusion }) {
  return (
    <div>
      {eyebrow && <div className="text-[10px] uppercase tracking-widest" style={{ color: EYEBROW_COLOR }}>{eyebrow}</div>}
      {title && <div className="mt-1 font-serif text-xl leading-snug" style={{ color: TITLE_COLOR }}>{title}</div>}
      {introduction && <div className="mt-2 font-serif text-sm italic" style={{ color: INTRO_COLOR }}>{introduction}</div>}
      <PhasesPreview phases={phases} />
      <ConclusionPreview conclusion={conclusion} />
    </div>
  );
}

function PhotoField({ label, helper, image, onUpload, onLibrary, onCrop, onRemove, onAltChange, uploading, testidPrefix }) {
  return (
    <div>
      <label className="mb-1 block text-[11px] text-espresso/65">{label}</label>
      {helper && <p className="mb-2 text-xs text-espresso/50">{helper}</p>}
      {image?.url ? (
        <div className="flex items-start gap-3">
          <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-espresso/10">
            <img src={resolveMediaUrl(image.url)} alt={image.alt || ""} className="h-full w-full object-cover"
              style={{ objectPosition: `${image.focal_x * 100}% ${image.focal_y * 100}%` }} />
          </div>
          <div className="flex-1 space-y-1.5">
            <input value={image.alt} onChange={(e) => onAltChange(e.target.value)}
              placeholder="Описание изображения (alt)"
              className="w-full rounded-lg border border-espresso/20 px-2 py-1 text-xs"
              data-testid={`${testidPrefix}-alt`} />
            <div className="flex flex-wrap gap-1.5">
              <button type="button" onClick={onUpload} disabled={uploading}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso disabled:opacity-50"
                data-testid={`${testidPrefix}-replace`}>
                {uploading ? "Загрузка…" : "Заменить фото"}
              </button>
              <button type="button" onClick={onLibrary}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso"
                data-testid={`${testidPrefix}-library`}>
                Из Медиатеки
              </button>
              <button type="button" onClick={onCrop}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso"
                data-testid={`${testidPrefix}-crop`}>
                Настроить кадр
              </button>
              <button type="button" onClick={onRemove}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso/70"
                data-testid={`${testidPrefix}-remove`}>
                Удалить фото
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <button type="button" onClick={onUpload} disabled={uploading}
            className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso disabled:opacity-50"
            data-testid={`${testidPrefix}-upload`}>
            {uploading ? "Загрузка…" : "Загрузить фото"}
          </button>
          <button type="button" onClick={onLibrary}
            className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso"
            data-testid={`${testidPrefix}-library-empty`}>
            Из Медиатеки
          </button>
        </div>
      )}
    </div>
  );
}

export function EditorialProcessStoryV2({ block, volume, onClose, onSaved, publicationDirty, onPublicationInput }) {
  const c = block.content || {};
  const initial = deriveInitial(c);

  const [sourceText, setSourceText] = useState(initial.sourceText);
  const [recognizedSourceText, setRecognizedSourceText] = useState(initial.recognizedSourceText);
  const [sourceEditing, setSourceEditing] = useState(false);

  const [eyebrow, setEyebrow] = useState(initial.eyebrow);
  const [title, setTitle] = useState(initial.title);
  const [introduction, setIntroduction] = useState(initial.introduction);
  const [phases, setPhases] = useState(initial.phases);
  const [conclusion, setConclusion] = useState(initial.conclusion);
  const [caption, setCaption] = useState(initial.caption);
  const [mainImage, setMainImage] = useState(initial.mainImage);
  const [mobileImage, setMobileImage] = useState(initial.mobileImage);

  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingMobile, setUploadingMobile] = useState(false);
  const [librarySlot, setLibrarySlot] = useState(null); // null | "main" | "mobile"
  const [cropSlot, setCropSlot] = useState(null); // null | "main" | "mobile"
  const mainFileInputRef = useRef(null);
  const mobileFileInputRef = useRef(null);

  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [saveState, setSaveState] = useState("idle");

  useEffect(() => {
    const next = deriveInitial(block.content || {});
    setSourceText(next.sourceText);
    setRecognizedSourceText(next.recognizedSourceText);
    setSourceEditing(false);
    setEyebrow(next.eyebrow);
    setTitle(next.title);
    setIntroduction(next.introduction);
    setPhases(next.phases);
    setConclusion(next.conclusion);
    setCaption(next.caption);
    setMainImage(next.mainImage);
    setMobileImage(next.mobileImage);
    setSaveState("idle");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id]);

  const destination = destinationLines(volume);
  const mobileActiveImage = mobileImage?.url ? mobileImage : mainImage;

  const hasStructuredResult = phases.length > 0 || Boolean(introduction.trim()) || Boolean(conclusion.trim());
  const isStale = sourceText.trim() !== recognizedSourceText.trim();
  const sourceNeedsRecognition = Boolean(sourceText.trim()) && isStale;
  // §1: a legacy block — predates source-first authoring — has an
  // existing structured result but no source_text at all. Never
  // fabricated/reverse-generated; purely a live read of current state.
  const hasLegacyStructuredResult = hasStructuredResult && !sourceText.trim();

  // ── Recognition ──────────────────────────────────────────────────
  const recognize = () => {
    const result = structureProcessStorySource(sourceText);
    setIntroduction(result.introduction);
    setPhases(result.phases);
    // The recognizer never invents a conclusion (see the .mjs file's
    // header) — carry forward whatever the author already typed there
    // across re-recognition, same as HERO/EDITORIAL_STORY do for their
    // own parser-blind manual fields, instead of wiping manual work.
    setConclusion((prev) => prev || result.conclusion);
    setRecognizedSourceText(sourceText);
    setSourceEditing(false);
  };

  const handleSourceTextChange = (value) => {
    setSourceText(value);
    // Staleness is derived from comparing sourceText to
    // recognizedSourceText on every render — nothing else to do here.
  };

  const returnToRecognized = () => setSourceEditing(false);

  // ── Phases — secondary correction controls, zero or more, never forced ──
  const addPhase = () => setPhases((rows) => [...rows, { id: newId("phase"), label: "", title: "", text: "", parameter: "" }]);
  const updatePhase = (index, field, value) => {
    setPhases((rows) => rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };
  const removePhase = (index) => setPhases((rows) => rows.filter((_, i) => i !== index));
  const movePhase = (index, dir) => {
    setPhases((rows) => {
      const to = index + dir;
      if (to < 0 || to >= rows.length) return rows;
      const next = [...rows];
      [next[index], next[to]] = [next[to], next[index]];
      return next;
    });
  };

  // ── Media ────────────────────────────────────────────────────────
  const pickFromLibrary = (media) => {
    const url = mediaRecordUrl(media) || media.file_url || media.url || null;
    const img = normalizeImage({ media_id: media.id, url });
    if (librarySlot === "mobile") setMobileImage(img);
    else setMainImage(img);
    setLibrarySlot(null);
  };

  const uploadTo = async (file, slot) => {
    const setUploading = slot === "mobile" ? setUploadingMobile : setUploadingMain;
    const setImage = slot === "mobile" ? setMobileImage : setMainImage;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(MEDIA_UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = mediaRecordUrl(data) || data.file_url || data.url || null;
      setImage(normalizeImage({ media_id: data.id, url }));
      toast.success("Фото загружено");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleMainFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) uploadTo(file, "main");
  };
  const handleMobileFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) uploadTo(file, "mobile");
  };

  const saveCrop = (values) => {
    if (cropSlot === "mobile") setMobileImage((img) => (img ? { ...img, ...values } : img));
    else if (cropSlot === "main") setMainImage((img) => (img ? { ...img, ...values } : img));
    setCropSlot(null);
  };

  // ── Save ─────────────────────────────────────────────────────────
  const save = async () => {
    if (sourceNeedsRecognition) return;
    setSaveState("saving");
    try {
      const nextContent = {
        ...c,
        process_story: {
          source_text: sourceText,
          eyebrow,
          title,
          introduction,
          caption,
          conclusion,
          main_image: mainImage ? { ...mainImage } : null,
          mobile_image: mobileImage ? { ...mobileImage } : null,
          phases: phases.map((p) => ({ id: p.id, label: p.label, title: p.title, text: p.text, parameter: p.parameter })),
        },
      };
      await api.patch(`/admin/editorial/blocks/${block.id}`, { content: nextContent });
      setSaveState("saved");
      const savedMediaItems = [mainImage, mobileImage]
        .filter((img) => img?.media_id && img?.url)
        .map((img) => ({ id: img.media_id, url: img.url }));
      onSaved?.({ ...block, content: nextContent }, savedMediaItems);
      toast.success("Изменения сохранены");
      setTimeout(() => setSaveState("idle"), 1500);
    } catch (e) {
      setSaveState("error");
      toast.error(formatApiError(e));
    }
  };

  const saveLabel = {
    idle: "", saving: "Сохранение…", saved: "Сохранено", error: "Ошибка сохранения",
  }[saveState];

  const workspaceTitle = title || "Технология";
  const cropImage = cropSlot === "mobile" ? mobileImage : cropSlot === "main" ? mainImage : null;
  const cropFrameLabel = cropSlot === "mobile" ? MOBILE_FRAME_LABEL : MAIN_FRAME_LABEL;

  return (
    <div
      onInputCapture={onPublicationInput} onChangeCapture={onPublicationInput}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="editorial-v2-processstory-workspace"
    >
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-cream">
        <div className="flex items-start justify-between gap-3 border-b border-espresso/10 px-5 py-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-espresso/55">Технология</div>
            <div className="mt-0.5 truncate font-serif text-xl text-espresso">{workspaceTitle}</div>
          </div>
          {saveLabel && (
            <span className="shrink-0 pt-1 text-[11px] uppercase tracking-widest text-espresso/50"
              data-testid="editorial-v2-processstory-save-state">
              {saveLabel}
            </span>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          <div className="rounded-2xl border border-espresso/10 bg-white/70 px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest text-espresso/55">Где будет опубликовано</div>
            <div className="mt-1 text-sm text-espresso/75" data-testid="editorial-v2-processstory-destination">
              {destination.title}
            </div>
            {destination.access && <div className="mt-0.5 text-xs text-espresso/55">{destination.access}</div>}
          </div>

          {/* Main image */}
          <PhotoField
            label="Основное изображение · необязательно"
            image={mainImage}
            uploading={uploadingMain}
            onUpload={() => mainFileInputRef.current?.click()}
            onLibrary={() => setLibrarySlot("main")}
            onCrop={() => setCropSlot("main")}
            onRemove={() => setMainImage(null)}
            onAltChange={(v) => setMainImage((img) => (img ? { ...img, alt: v } : img))}
            testidPrefix="editorial-v2-processstory-main"
          />
          <input ref={mainFileInputRef} type="file" accept="image/*" className="hidden"
            onChange={handleMainFile} data-testid="editorial-v2-processstory-main-file-input" />

          {/* Mobile image */}
          <PhotoField
            label="Мобильное изображение · необязательно"
            helper="Если не добавлено, на телефоне используется основное изображение."
            image={mobileImage}
            uploading={uploadingMobile}
            onUpload={() => mobileFileInputRef.current?.click()}
            onLibrary={() => setLibrarySlot("mobile")}
            onCrop={() => setCropSlot("mobile")}
            onRemove={() => setMobileImage(null)}
            onAltChange={(v) => setMobileImage((img) => (img ? { ...img, alt: v } : img))}
            testidPrefix="editorial-v2-processstory-mobile"
          />
          <input ref={mobileFileInputRef} type="file" accept="image/*" className="hidden"
            onChange={handleMobileFile} data-testid="editorial-v2-processstory-mobile-file-input" />

          {/* Manual, recognition-independent fields */}
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Метка раздела</label>
              <input value={eyebrow} onChange={(e) => setEyebrow(e.target.value)}
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-processstory-eyebrow" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Заголовок</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                data-testid="editorial-v2-processstory-title" />
            </div>
          </div>

          {/* §2: legacy entry point — a block that predates source-first
              authoring gets an explicit, prominent way in, without
              losing its existing structured result. */}
          {hasLegacyStructuredResult && !sourceEditing && (
            <div className="rounded-2xl border border-espresso/20 bg-white px-4 py-4">
              <div className="text-sm font-medium text-espresso">Новый способ подготовки текста</div>
              <p className="mt-1 text-xs text-espresso/60">
                Вставьте технологический текст целиком — Редакция распознает этапы процесса и ключевые параметры.
                Текущий результат сохранится, пока вы не запустите распознавание заново.
              </p>
              <button type="button" onClick={() => setSourceEditing(true)}
                className="mt-3 rounded-xl bg-espresso px-5 py-2.5 text-sm text-cream"
                data-testid="editorial-v2-processstory-legacy-entry">
                Вставить исходный текст технологии
              </button>
            </div>
          )}

          {/* Source-first recognition flow */}
          {(!hasStructuredResult || sourceEditing) && (
            <div>
              <label className="mb-1 block text-[11px] text-espresso/65">Исходный текст технологии</label>
              <textarea
                value={sourceText}
                onChange={(e) => handleSourceTextChange(e.target.value)}
                rows={10}
                placeholder={"Вставьте технологический текст целиком.\nРедакция выделит этапы процесса и ключевые параметры."}
                className="w-full rounded-xl border border-espresso/20 bg-white px-3 py-3 text-sm leading-relaxed outline-none focus:border-espresso/45"
                data-testid="editorial-v2-processstory-source"
              />
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <button type="button" onClick={recognize} disabled={!sourceText.trim()}
                  className="rounded-xl bg-espresso px-5 py-2.5 text-sm text-cream disabled:opacity-40"
                  data-testid="editorial-v2-processstory-recognize">
                  Распознать технологию
                </button>
                {hasStructuredResult && !isStale && (
                  <button type="button" onClick={returnToRecognized}
                    className="text-xs text-espresso/60 underline underline-offset-2"
                    data-testid="editorial-v2-processstory-return-to-recognized">
                    Вернуться к подготовленному тексту
                  </button>
                )}
              </div>
              {hasStructuredResult && isStale && (
                <p className="mt-2 text-xs text-espresso/60" data-testid="editorial-v2-processstory-stale">
                  Текст изменён — распознайте структуру заново.
                </p>
              )}
            </div>
          )}

          {/* Structured result — the primary reviewed artifact */}
          {hasStructuredResult && (
            <div className="rounded-2xl border border-espresso/15 bg-white/70 px-4 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-medium text-espresso">
                  <span aria-hidden="true">✓</span>
                  <span>Результат распознавания</span>
                </div>
                {!hasLegacyStructuredResult && (
                  <button type="button" onClick={() => setSourceEditing(true)}
                    className="text-xs text-espresso/60 underline underline-offset-2"
                    data-testid="editorial-v2-processstory-edit-source">
                    Изменить исходный текст
                  </button>
                )}
              </div>

              <div className="mt-3 space-y-3">
                <div>
                  <label className="mb-1 block text-[11px] text-espresso/60">Введение</label>
                  <textarea value={introduction} onChange={(e) => setIntroduction(e.target.value)} rows={3}
                    className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                    data-testid="editorial-v2-processstory-introduction" />
                </div>

                <div>
                  <div className="mb-1 text-[11px] text-espresso/60">Фазы процесса</div>
                  <div className="space-y-2">
                    {phases.map((p, i) => (
                      <div key={p.id} className="rounded-lg border border-espresso/10 p-2">
                        <div className="mb-1 text-[10px] uppercase tracking-widest text-espresso/40">
                          {String(i + 1).padStart(2, "0")}
                        </div>
                        <div className="space-y-1.5">
                          <input value={p.label} onChange={(e) => updatePhase(i, "label", e.target.value)}
                            placeholder="Метка фазы · необязательно"
                            className="w-full rounded-lg border border-espresso/15 px-2 py-1 text-xs"
                            data-testid={`editorial-v2-processstory-phase-label-${i}`} />
                          <input value={p.title} onChange={(e) => updatePhase(i, "title", e.target.value)}
                            placeholder="Заголовок фазы"
                            className="w-full rounded-lg border border-espresso/15 px-2 py-1 text-xs"
                            data-testid={`editorial-v2-processstory-phase-title-${i}`} />
                          <textarea value={p.text} onChange={(e) => updatePhase(i, "text", e.target.value)} rows={3}
                            placeholder="Что происходит и почему"
                            className="w-full rounded-lg border border-espresso/15 px-2 py-1 text-xs"
                            data-testid={`editorial-v2-processstory-phase-text-${i}`} />
                          <input value={p.parameter} onChange={(e) => updatePhase(i, "parameter", e.target.value)}
                            placeholder="Ключевой параметр · необязательно"
                            className="w-full rounded-lg border border-espresso/15 px-2 py-1 text-xs"
                            data-testid={`editorial-v2-processstory-phase-parameter-${i}`} />
                        </div>
                        {/* Secondary correction controls — intentionally understated. */}
                        <div className="mt-1.5 flex gap-3 text-[10px] text-espresso/45">
                          <button type="button" onClick={() => movePhase(i, -1)} disabled={i === 0}
                            className="underline underline-offset-2 disabled:no-underline disabled:opacity-40"
                            data-testid={`editorial-v2-processstory-phase-up-${i}`}>
                            вверх
                          </button>
                          <button type="button" onClick={() => movePhase(i, 1)} disabled={i === phases.length - 1}
                            className="underline underline-offset-2 disabled:no-underline disabled:opacity-40"
                            data-testid={`editorial-v2-processstory-phase-down-${i}`}>
                            вниз
                          </button>
                          <button type="button" onClick={() => removePhase(i)}
                            className="underline underline-offset-2"
                            data-testid={`editorial-v2-processstory-phase-remove-${i}`}>
                            удалить
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={addPhase}
                    className="mt-2 text-[11px] text-espresso/45 underline underline-offset-2"
                    data-testid="editorial-v2-processstory-phase-add">
                    + добавить фазу вручную
                  </button>
                </div>

                <div>
                  <label className="mb-1 block text-[11px] text-espresso/60">Заключение</label>
                  <textarea value={conclusion} onChange={(e) => setConclusion(e.target.value)} rows={2}
                    className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
                    data-testid="editorial-v2-processstory-conclusion" />
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="mb-1 block text-[11px] text-espresso/60">Подпись к изображению</label>
            <input value={caption} onChange={(e) => setCaption(e.target.value)}
              className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm"
              data-testid="editorial-v2-processstory-caption" />
          </div>

          {/* Local preview — mirrors the confirmed ProcessStoryMaster Reader contract */}
          {showPreview && (
            <div className="rounded-2xl border border-espresso/15 bg-white p-4" data-testid="editorial-v2-processstory-preview">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-espresso/50">Предпросмотр разворота</div>
                <div className="flex gap-1 rounded-full border border-espresso/15 p-0.5 text-[11px]">
                  <button type="button" onClick={() => setPreviewDevice("desktop")}
                    className={`rounded-full px-2.5 py-1 ${previewDevice === "desktop" ? "bg-espresso text-cream" : "text-espresso/60"}`}
                    data-testid="editorial-v2-processstory-preview-desktop">
                    Desktop
                  </button>
                  <button type="button" onClick={() => setPreviewDevice("mobile")}
                    className={`rounded-full px-2.5 py-1 ${previewDevice === "mobile" ? "bg-espresso text-cream" : "text-espresso/60"}`}
                    data-testid="editorial-v2-processstory-preview-mobile">
                    Mobile
                  </button>
                </div>
              </div>

              {previewDevice === "desktop" ? (
                mainImage?.url ? (
                  <div className="mx-auto" style={{ maxWidth: "1200px", backgroundColor: BG }}>
                    <div className="p-4">
                      <div className="grid gap-3 lg:gap-4" style={{ gridTemplateColumns: "minmax(0,45fr) minmax(0,55fr)" }}>
                        <div>
                          <ProcessImage image={mainImage} maxHeight={DESKTOP_MAX_HEIGHT} />
                          {caption && (
                            <div className="mt-2 text-[11px] italic" style={{ color: CAPTION_COLOR }}>{caption}</div>
                          )}
                        </div>
                        <ProcessTextBlock eyebrow={eyebrow} title={title} introduction={introduction}
                          phases={phases} conclusion={conclusion} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mx-auto" style={{ maxWidth: "760px", backgroundColor: BG }}>
                    <div className="px-6 py-10">
                      <ProcessTextBlock eyebrow={eyebrow} title={title} introduction={introduction}
                        phases={phases} conclusion={conclusion} />
                    </div>
                  </div>
                )
              ) : (
                <div className="mx-auto max-w-[240px]" style={{ backgroundColor: BG }}>
                  <div className="p-3">
                    {(eyebrow || title || introduction) && (
                      <div>
                        {eyebrow && <div className="text-[10px] uppercase tracking-widest" style={{ color: EYEBROW_COLOR }}>{eyebrow}</div>}
                        {title && <div className="mt-1 font-serif text-lg leading-snug" style={{ color: TITLE_COLOR }}>{title}</div>}
                        {introduction && <div className="mt-2 font-serif text-sm italic" style={{ color: INTRO_COLOR }}>{introduction}</div>}
                      </div>
                    )}
                    {mobileActiveImage?.url && (
                      <div className="mt-3">
                        <ProcessImage image={mobileActiveImage} maxHeight={MOBILE_MAX_HEIGHT} />
                      </div>
                    )}
                    <PhasesPreview phases={phases} />
                    <ConclusionPreview conclusion={conclusion} />
                    {/* Mobile-only: caption renders LAST, after phases and conclusion. */}
                    {mobileActiveImage?.url && caption && (
                      <div className="mt-4 text-[11px] italic" style={{ color: CAPTION_COLOR }}>{caption}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {sourceNeedsRecognition && (
          <div className="border-t border-espresso/10 bg-cream/70 px-5 py-2 text-center text-xs text-espresso/60"
            data-testid="editorial-v2-processstory-save-guard">
            Текст изменён — распознайте структуру заново.
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-espresso/10 px-5 py-4">
          <EditorialSpreadPublicationControlsV1 block={block} dirty={publicationDirty} />
          <button type="button" onClick={onClose}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso"
            data-testid="editorial-v2-processstory-close">
            Закрыть
          </button>
          <button type="button" onClick={() => setShowPreview((v) => !v)}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso"
            data-testid="editorial-v2-processstory-preview-toggle">
            {showPreview ? "Скрыть предпросмотр" : "Предпросмотр разворота"}
          </button>
          <button type="button" onClick={save} disabled={saveState === "saving" || sourceNeedsRecognition}
            className="rounded-xl bg-espresso px-4 py-2 text-sm text-cream disabled:opacity-50"
            data-testid="editorial-v2-processstory-save">
            Сохранить изменения
          </button>
        </div>
      </div>

      <MediaLibraryPickerV2 open={librarySlot !== null} onClose={() => setLibrarySlot(null)} onPick={pickFromLibrary} />

      <EditorialCropV2
        open={cropSlot !== null}
        url={resolveMediaUrl(cropImage?.url || "")}
        aspectRatio={CROP_ASPECT}
        frameLabel={cropFrameLabel}
        focalX={cropImage?.focal_x ?? 0.5}
        focalY={cropImage?.focal_y ?? 0.5}
        zoom={cropImage?.zoom ?? 1}
        onSave={saveCrop}
        onClose={() => setCropSlot(null)}
      />
    </div>
  );
}